/**
 * React Query hooks for user profile management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';

// ============================================================================
// Types
// ============================================================================

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  favorite_team_code: string | null;
  is_public: boolean;
  is_admin: boolean;
  is_moderator: boolean;
  is_super_admin: boolean;
  is_beta_tester: boolean;
  scorecard_count: number;
  subscription_tier: string;
  username_changed_at: string | null;
  can_change_username: boolean;
  username_cooldown_days: number;
  // Community features
  has_completed_onboarding: boolean;
  follower_count: number;
  following_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateProfileData {
  username: string;
  display_name?: string;
  favorite_team_code?: string;
  accepted_terms_at?: string;
}

export interface UpdateProfileData {
  username?: string;
  display_name?: string;
  bio?: string;
  favorite_team_code?: string;
  is_public?: boolean;
}

export interface UsernameAvailability {
  username: string;
  available: boolean;
  message: string | null;
}

export interface UsernameChangeStatus {
  can_change: boolean;
  days_remaining: number;
  message: string;
}

export interface HasProfile {
  has_profile: boolean;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch the current user's profile.
 * Only enabled when user is signed in.
 */
export function useUserProfile() {
  const { isSignedIn } = useAuth();
  const authFetch = useAuthenticatedFetch();

  return useQuery<UserProfile>({
    queryKey: ['user-profile'],
    queryFn: () => authFetch('/api/v2/users/me'),
    enabled: isSignedIn,
    retry: (failureCount, error) => {
      // Don't retry 404s (user hasn't created profile yet)
      if (error?.message?.includes('404')) return false;
      // Retry auth errors up to 3 times (Clerk token may not be ready yet)
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    staleTime: STALE_TIMES.LIVE_DATA,
  });
}

/**
 * Check if the current user has created a profile.
 * Used to determine if username setup is needed.
 */
export function useHasProfile() {
  const { isSignedIn } = useAuth();
  const authFetch = useAuthenticatedFetch();

  return useQuery<HasProfile>({
    queryKey: ['has-profile'],
    queryFn: () => authFetch('/api/v2/users/has-profile'),
    enabled: isSignedIn,
    retry: false, // Don't retry on logout race condition
    retryOnMount: false, // Don't re-fetch errored queries when new components mount
    staleTime: STALE_TIMES.LIVE_DATA,
  });
}

/**
 * Get username change cooldown status.
 */
export function useUsernameChangeStatus() {
  const { isSignedIn } = useAuth();
  const authFetch = useAuthenticatedFetch();

  return useQuery<UsernameChangeStatus>({
    queryKey: ['username-change-status'],
    queryFn: () => authFetch('/api/v2/users/me/username-status'),
    enabled: isSignedIn,
    staleTime: STALE_TIMES.PLAYER_INFO,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Check if a username is available.
 * Only queries when username is at least 3 characters.
 */
export function useCheckUsername(username: string) {
  const authFetch = useAuthenticatedFetch();

  return useQuery<UsernameAvailability>({
    queryKey: ['check-username', username],
    queryFn: () =>
      authFetch(`/api/v2/users/check-username?username=${encodeURIComponent(username)}`),
    enabled: username.length >= 3,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new user profile (first-time setup).
 */
export function useCreateProfile() {
  const queryClient = useQueryClient();
  const authFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (data: CreateProfileData) =>
      authFetch<UserProfile>('/api/v2/users/me', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['has-profile'] });
    },
  });
}

/**
 * Update the current user's profile.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const authFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (data: UpdateProfileData) =>
      authFetch<UserProfile>('/api/v2/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['username-change-status'] });
    },
  });
}

/**
 * Sync avatar URL from Clerk.
 */
export function useSyncAvatar() {
  const queryClient = useQueryClient();
  const authFetch = useAuthenticatedFetch();

  return useMutation({
    mutationFn: (avatar_url: string | null) =>
      authFetch<UserProfile>('/api/v2/users/me/sync-avatar', {
        method: 'POST',
        body: JSON.stringify({ avatar_url }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });
}
